// /profile/documents — upload + manage FASR Sport ID and insurance docs.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Section, Cell, List, Button, Caption, Spinner, Banner, Title, Input,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const KIND_META = {
  fasr_id:   { label: "ID ФАСР Sport", icon: "🪪", note: "Карточка ФАСР или скан подтверждающего письма." },
  insurance: { label: "Страховка",     icon: "🧾", note: "PDF или фото полиса. Срок действия укажите ниже." },
};

const STATUS_META = {
  pending:  { tone: "warn",   text: "На проверке" },
  verified: { tone: "ok",     text: "Подтверждён" },
  rejected: { tone: "danger", text: "Отклонён" },
};

export default function Documents() {
  const navigate = useNavigate();
  usePageBack("/profile");
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  function load() {
    api("/api/v1/me/documents")
      .then((r) => setDocs(r.documents || []))
      .catch((e) => setError(`Не удалось загрузить документы: ${e.message}`));
  }
  useEffect(load, []);

  async function uploadOne({ kind, contentType, expiresOn, dataBase64 }) {
    setError(null);
    try {
      await api("/api/v1/me/documents", {
        method: "POST",
        body: JSON.stringify({ kind, content_type: contentType, expires_on: expiresOn || null, data: dataBase64 }),
      });
      load();
    } catch (e) {
      setError(e.message || "Не удалось загрузить файл.");
    }
  }

  async function removeOne(id) {
    if (!window.confirm("Удалить документ? После удаления нужно будет загрузить заново.")) return;
    try {
      await api(`/api/v1/me/documents/${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setError(e.message || "Не удалось удалить.");
    }
  }

  if (error && !docs) return <List><Banner type="section" header="Ошибка">{error}</Banner></List>;
  if (!docs) return <List><Section><Cell before={<Spinner size="m" />}>Загружаем…</Cell></Section></List>;

  const grouped = { fasr_id: [], insurance: [] };
  for (const d of docs) (grouped[d.kind] || []).push(d);

  return (
    <List>
      {error && <Banner type="section" header="Ошибка">{error}</Banner>}
      <Section header="Документы пилота">
        <Cell>
          <Caption level="1">
            Хорошее фото: документ полностью в кадре, без бликов, читаемый текст. Качество влияет на скорость подтверждения.
          </Caption>
        </Cell>
      </Section>

      {Object.entries(KIND_META).map(([kind, meta]) => (
        <DocSection
          key={kind}
          kind={kind}
          meta={meta}
          docs={grouped[kind] || []}
          onUpload={uploadOne}
          onDelete={removeOne}
        />
      ))}

      <Section>
        <Cell onClick={() => navigate("/profile")}>← Вернуться в профиль</Cell>
      </Section>
    </List>
  );
}

function DocSection({ kind, meta, docs, onUpload, onDelete }) {
  const [expiresOn, setExpiresOn] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  async function pickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataBase64 = await readAsBase64(file);
      await onUpload({
        kind,
        contentType: file.type || "application/octet-stream",
        expiresOn: expiresOn || null,
        dataBase64,
      });
      setExpiresOn("");
    } finally {
      setBusy(false);
      e.target.value = ""; // allow re-uploading same file name
    }
  }

  return (
    <Section header={`${meta.icon} ${meta.label}`}>
      {docs.length === 0 && (
        <Cell subtitle={meta.note} multiline>Пока ничего не загружено</Cell>
      )}
      {docs.map((d) => (
        <DocRow key={d.id} doc={d} onDelete={() => onDelete(d.id)} />
      ))}
      <Cell>
        <Input
          header="Срок действия (необязательно)"
          type="date"
          placeholder="ДД.ММ.ГГГГ"
          value={expiresOn}
          onChange={(e) => setExpiresOn(e.target.value)}
        />
      </Cell>
      <Cell>
        <Button
          stretched
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? "Загружаем…" : `📷 Загрузить ${meta.label}`}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          capture="environment"
          disabled={busy}
          onChange={pickFile}
          style={{ display: "none" }}
        />
      </Cell>
    </Section>
  );
}

function DocRow({ doc, onDelete }) {
  const status = STATUS_META[doc.status] || STATUS_META.pending;
  return (
    <>
      <Cell
        subtitle={
          [
            new Date(doc.uploaded_at).toLocaleDateString("ru-RU"),
            doc.expires_on ? `до ${new Date(doc.expires_on).toLocaleDateString("ru-RU")}` : null,
            `${Math.round(doc.size_bytes / 1024)} КБ`,
          ].filter(Boolean).join(" · ")
        }
        after={<span className={`rcf-pill rcf-pill--${status.tone}`}>{status.text}</span>}
        multiline
      >
        Документ #{doc.id}
      </Cell>
      {doc.status === "rejected" && doc.rejection_reason && (
        <Cell subtitle={doc.rejection_reason} multiline>Причина отказа</Cell>
      )}
      <Cell>
        <Button stretched mode="plain" onClick={onDelete}>🗑 Удалить и загрузить заново</Button>
      </Cell>
    </>
  );
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result || "");
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    r.onerror = () => reject(new Error("Не удалось прочитать файл."));
    r.readAsDataURL(file);
  });
}
